import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { MediaProvider } from './media-context';

// App-level static config that is constant for the whole client render.
export type UIConfigContextData = {
  deviceType: 'DESKTOP' | 'TABLET' | 'MOBILE';
  isAdmiralCookieBannerEnabled: boolean;
  resourcesUrl: string;
};

export const UIConfigContext = createContext<UIConfigContextData | undefined>(undefined);
UIConfigContext.displayName = 'UIConfigContext';

export const UIConfigProvider = ({
  children,
  deviceType,
  isAdmiralCookieBannerEnabled,
  resourcesUrl,
}: PropsWithChildren<UIConfigContextData>) => {
  const value = useMemo(
    () => ({ deviceType, isAdmiralCookieBannerEnabled, resourcesUrl }),
    [deviceType, isAdmiralCookieBannerEnabled, resourcesUrl],
  );

  // MediaProvider is the single source of client device detection (one resize listener, one
  // device-cookie writer), so every UIConfigProvider consumer, test wrappers included, gets useMedia.
  // It takes the server deviceType seed by prop so media-context never imports ui-config-context.
  return (
    <UIConfigContext.Provider value={value}>
      <MediaProvider deviceType={deviceType}>{children}</MediaProvider>
    </UIConfigContext.Provider>
  );
};

export const useUIConfig = () => {
  const context = useContext(UIConfigContext);
  if (!context) {
    throw new Error('useUIConfig can only be used inside of UIConfigProvider');
  }

  return context;
};
